/**
 * Using a higher DPI (dots per inch) for UI rendering serves several important purposes.
 * Firstly, it enhances clarity and detail in images and text, ensuring that all elements are crisp
 * and easy to read. This is particularly crucial for graphic components, such as icons and text,
 * where every detail matters for user perception. Secondly, higher DPI improves print quality,
 * making digital materials look just as good on paper as they do on screen.
 * Additionally, with the prevalence of high-resolution displays, such as Retina screens,
 * utilizing a higher DPI ensures that content is rendered beautifully on these devices.
 * Ultimately, a higher DPI contributes to an improved user experience, offering clearer,
 * more professional visuals that facilitate user engagement and information retention.
 */
const dpi = 72;
const viewDPI = 109; // Apple Macs displays have a higher resolution of 109 ppi

/**
 * Converts inches to pixels based on the specified view DPI (dots per inch).
 *
 * @param inch - The value in inches to be converted to pixels.
 * @returns The equivalent value in pixels.
 *
 * @example
 * // Convert A4 width (8.27 inches) to pixels
 * const widthInPixels = inchesToPixels(8.27); // returns 901
 *
 * // Convert A4 height (11.69 inches) to pixels
 * const heightInPixels = inchesToPixels(11.69); // returns 1274
 */
export const inchesToPixels = (inch: number): number => {
  return Math.floor(inch * viewDPI);
};

/**
 * Converts millimeters to pixels based on the specified view DPI (dots per inch).
 *
 * @param mm - The value in millimeters to be converted to pixels.
 * @returns The equivalent value in pixels.
 *
 * @example
 * // Convert A4 width (210 mm) to pixels
 * const widthInPixels = mmToPixels(210); // returns 901
 *
 * // Convert A4 height (297 mm) to pixels
 * const heightInPixels = mmToPixels(297); // returns 1274
 */
export const mmToPixels = (mm: number): number => {
  const inches = mm / 25.4; // 1 inch = 25.4 mm
  return Math.floor(inches * viewDPI);
};

/**
 * Converts pixels to points based on the specified DPI (dots per inch).
 *
 * @param pixels - The value in pixels to be converted to points.
 * @returns The equivalent value in points.
 *
 * @example
 * // Convert A4 width in pixels (901) to points
 * const widthInPoints = pixelsToPoints(901); // returns 682
 *
 * // Convert A4 height in pixels (1274) to points
 * const heightInPoints = pixelsToPoints(1274); // returns 952
 */
export const pixelsToPoints = (pixels: number): number => {
  return Math.round((pixels / viewDPI) * dpi);
};
